/**
 * Permission Hook
 * React hook for permission checking and UI state management
 */

import { useState, useEffect, useCallback } from "react";
import {
  usePermissions as getPermissions,
  getBusinessCreationStatus,
} from "../services/permissionService";
import {
  BusinessCreationStatus,
  UserPermissionsResponse,
} from "../services/permissionService";

export interface PermissionState {
  canCreateBusiness: boolean;
  canViewBusinessMenu: boolean;
  canEditBusiness: boolean;
  canDeleteBusiness: boolean;
  canManageBusinessServices: boolean;
  canManageBusinessEmployees: boolean;
  canViewBusinessAnalytics: boolean;
  canManageClients: boolean;
  canViewAppointments: boolean;
  canManageAppointments: boolean;
  canAccessSupport: boolean;
  canManageSupportTickets: boolean;
  canManageUsers: boolean;
  canManagePackages: boolean;
  canManageCategories: boolean;
  canManageSystemSettings: boolean;
  canViewAdminDashboard: boolean;
  canViewAllBusinesses: boolean;
  canViewAllClients: boolean;
}

export const usePermissionState = () => {
  const permissions = getPermissions();
  const [permissionState, setPermissionState] = useState<PermissionState>({
    canCreateBusiness: false,
    canViewBusinessMenu: false,
    canEditBusiness: false,
    canDeleteBusiness: false,
    canManageBusinessServices: false,
    canManageBusinessEmployees: false,
    canViewBusinessAnalytics: false,
    canManageClients: false,
    canViewAppointments: false,
    canManageAppointments: false,
    canAccessSupport: false,
    canManageSupportTickets: false,
    canManageUsers: false,
    canManagePackages: false,
    canManageCategories: false,
    canManageSystemSettings: false,
    canViewAdminDashboard: false,
    canViewAllBusinesses: false,
    canViewAllClients: false,
  });

  const [loading, setLoading] = useState(true);
  const [businessCreationStatus, setBusinessCreationStatus] =
    useState<BusinessCreationStatus | null>(null);
  const [userPermissions, setUserPermissions] =
    useState<UserPermissionsResponse | null>(null);

  const loadPermissions = useCallback(async () => {
    try {
      setLoading(true);

      // Load all permissions in parallel
      const [
        canCreateBusiness,
        canViewBusinessMenu,
        canEditBusiness,
        canDeleteBusiness,
        canManageBusinessServices,
        canManageBusinessEmployees,
        canViewBusinessAnalytics,
        canManageClients,
        canViewAppointments,
        canManageAppointments,
        canAccessSupport,
        canManageSupportTickets,
        canManageUsers,
        canManagePackages,
        canManageCategories,
        canManageSystemSettings,
        canViewAdminDashboard,
        canViewAllBusinesses,
        canViewAllClients,
      ] = await Promise.all([
        permissions.canCreateBusiness(),
        permissions.canViewBusinessMenu(),
        permissions.canEditBusiness(),
        permissions.canDeleteBusiness(),
        permissions.canManageBusinessServices(),
        permissions.canManageBusinessEmployees(),
        permissions.canViewBusinessAnalytics(),
        permissions.canManageClients(),
        permissions.canViewAppointments(),
        permissions.canManageAppointments(),
        permissions.canAccessSupport(),
        permissions.canManageSupportTickets(),
        permissions.canManageUsers(),
        permissions.canManagePackages(),
        permissions.canManageCategories(),
        permissions.canManageSystemSettings(),
        permissions.canViewAdminDashboard(),
        permissions.canViewAllBusinesses(),
        permissions.canViewAllClients(),
      ]);

      setPermissionState({
        canCreateBusiness,
        canViewBusinessMenu,
        canEditBusiness,
        canDeleteBusiness,
        canManageBusinessServices,
        canManageBusinessEmployees,
        canViewBusinessAnalytics,
        canManageClients,
        canViewAppointments,
        canManageAppointments,
        canAccessSupport,
        canManageSupportTickets,
        canManageUsers,
        canManagePackages,
        canManageCategories,
        canManageSystemSettings,
        canViewAdminDashboard,
        canViewAllBusinesses,
        canViewAllClients,
      });
    } catch (error) {
      console.error("Error loading permissions:", error);
    } finally {
      setLoading(false);
    }
  }, [permissions]);

  const loadBusinessCreationStatus = useCallback(async () => {
    try {
      const status = await permissions.canCreateBusiness();
      setBusinessCreationStatus({
        can_create: status,
        has_permission: status,
      });
    } catch (error) {
      console.error("Error loading business creation status:", error);
    }
  }, [permissions]);

  const loadUserPermissions = useCallback(async () => {
    try {
      // For now, we'll just set it to null as we don't need detailed permission list for UI
      setUserPermissions(null);
    } catch (error) {
      console.error("Error loading user permissions:", error);
    }
  }, []);

  useEffect(() => {
    loadPermissions();
    loadBusinessCreationStatus();
    loadUserPermissions();
  }, [loadPermissions, loadBusinessCreationStatus, loadUserPermissions]);

  const refreshPermissions = useCallback(() => {
    loadPermissions();
    loadBusinessCreationStatus();
  }, [loadPermissions, loadBusinessCreationStatus]);

  return {
    ...permissionState,
    loading,
    businessCreationStatus,
    userPermissions,
    refreshPermissions,
  };
};

/**
 * Hook for checking a specific permission
 */
export const usePermission = (permissionName: string) => {
  const [hasPermission, setHasPermission] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkPermission = useCallback(async () => {
    try {
      setLoading(true);
      const permissions = getPermissions();
      const result = await permissions.checkUserPermission(permissionName);
      setHasPermission(result);
    } catch (error) {
      console.error(`Error checking permission ${permissionName}:`, error);
      setHasPermission(false);
    } finally {
      setLoading(false);
    }
  }, [permissionName]);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  return { hasPermission, loading, refresh: checkPermission };
};

/**
 * Hook for business creation status
 */
export const useBusinessCreationStatus = () => {
  const [status, setStatus] = useState<BusinessCreationStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getBusinessCreationStatus();
      setStatus(result);
    } catch (error) {
      console.error("Error loading business creation status:", error);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  return { status, loading, refresh: loadStatus };
};
